import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useScheduledSongs } from '../../context';

interface RecentlyScheduledSidebarProps {
  className?: string;
  isLoading?: boolean;
}

const RecentlyScheduledSidebar: React.FC<RecentlyScheduledSidebarProps> = ({ className, isLoading = false }) => {
  const { recentScheduledSongs } = useScheduledSongs();
  const navigate = useNavigate();

  // Get the top 3 most recent scheduled songs with null safety
  const recentSongs = Array.isArray(recentScheduledSongs) ? recentScheduledSongs.slice(0, 3) : [];

  const handleLoadMore = () => {
    navigate('/schedule_tracks');
  };

  const handleItemClick = () => {
    navigate('/schedule_tracks');
  };

  return (
    <div className={className}>
      <div className="section-header">
        <div className="section-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <span className="section-title">Recently scheduled</span>
      </div>

      <div className="recently-scheduled-items">
        {Array.isArray(recentSongs) && recentSongs.map((song, index) => {
          // Comprehensive null checks for all nested properties
          const trackName = song?.name || song?.localData?.song_name || 'Unknown Track';
          const artistName = (Array.isArray(song?.artists) && song.artists.length > 0 && song.artists[0]?.name) 
            || song?.localData?.artist_name 
            || 'Unknown Artist';
          const imageUrl = (Array.isArray(song?.album?.images) && song.album.images.length > 0 && song.album.images[0]?.url) 
            || '/default-album.png';

          return (
            <div 
              key={song._id || index} 
              className="recently-scheduled-item"
              onClick={handleItemClick}
            >
              <div className="item-image">
                {imageUrl ? (
                  <img src={imageUrl} alt={trackName} />
                ) : (
                  <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
                    <rect width="48" height="48" rx="8" fill="#2A2A2A" />
                    <path
                      d="M24 34C29.5228 34 34 29.5228 34 24C34 18.4772 29.5228 14 24 14C18.4772 14 14 18.4772 14 24C14 29.5228 18.4772 34 24 34Z"
                      fill="#404040"
                    />
                  </svg>
                )}
              </div>
              <div className="item-info">
                <div className="item-title" title={trackName}>{trackName}</div>
                <div className="item-artist" title={artistName}>{artistName}</div>
              </div>
            </div>
          );
        })}
      </div>

      {isLoading ? (
        <div className="recently-scheduled-loading">
          <div className="loading-spinner">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ animation: 'spin 1s linear infinite' }}
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeDasharray="31.4 31.4"
                strokeDashoffset="31.4"
                style={{ animation: 'dash 1.5s ease-in-out infinite' }}
              />
            </svg>
          </div>
          <span>Loading...</span>
        </div>
      ) : !Array.isArray(recentScheduledSongs) || recentScheduledSongs.length === 0 ? (
        <div className="no-scheduled-songs">
          <p>No scheduled songs</p>
          <div className="load-more-link" onClick={handleLoadMore}>
            + Schedule songs
          </div>
        </div>
      ) : (
        <div className="load-more-link" onClick={handleLoadMore}>
          + Load more songs
        </div>
      )}
    </div>
  );
};

export default RecentlyScheduledSidebar;
